// Hub admin page: /admin/qa
//
// Quality & Operations Portal — the test-discipline + defect-tracking +
// DD-artifact-index hub. Designed to look great during technical due
// diligence. corporate_admin gated.
//
// Five internal tabs:
//   - Dashboard  — KPIs (total cases, P0 count, last run pass rate, open defects)
//   - Cases      — every test case, filterable
//   - Runs       — historical runs + "Start new run" → execution mode
//   - Defects    — bug tracker (file, status, severity, fix verification)
//   - Artifacts  — curated DD-relevant docs/links
//
// All data lives in Supabase tables created in migration
// `create_dd_portal_schema`. RLS gates access to corporate_admin.

import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  listTestCases, listTestRuns, listResultsForRun, listDefects, listDDArtifacts,
  listBuildReleases,
  getDashboardSummary, getRecentRunsPassRates, getSectionStats, getDefectStats, QA_NOT_ON_AWS, cellQaAdmitted,
  createTestRun, completeTestRun, recordResult,
  fileDefect, updateDefect,
  type TestCase, type TestRun, type TestResult, type Defect, type DDArtifact,
  type BuildRelease,
  type ResultStatus, type DefectSeverity, type DefectStatus, type Priority, type TestType, type RunEnvironment,
} from '@/lib/qa';
import { isCell } from '@/lib/cell/config';
import { useSession, useOperator } from '@/lib/auth';

type Tab = 'dashboard' | 'cases' | 'runs' | 'defects' | 'builds' | 'operations' | 'artifacts';

const ALL_TABS: Tab[] = ['dashboard', 'cases', 'runs', 'defects', 'builds', 'operations', 'artifacts'];

/** CELL (M28, Cora 2026-10-30): on the cell, whether the cell admits this person to the QA doors (lib/qa.ts cellQaAdmitted).
 *  null while asking; off the cell it is never asked. */
function useCellQaAdmitted(): boolean | null {
  const [admitted, setAdmitted] = useState<boolean | null>(null);
  useEffect(() => {
    if (!isCell()) return;
    let live = true;
    void cellQaAdmitted().then((a) => { if (live) setAdmitted(a); });
    return () => { live = false; };
  }, []);
  return admitted;
}

export default function QAPortal() {
  const { operator } = useOperator();
  const cellAdmitted = useCellQaAdmitted();
  const { session } = useSession();
  const location = useLocation();
  const navigate = useNavigate();

  // The URL hash IS the tab — single source of truth. This lets
  // artifact cards and gate links deep-link into specific tabs
  // (e.g. /admin/qa#builds) and stay in sync as react-router
  // navigates, with no separate hashchange listener.
  const tab: Tab = useMemo(() => {
    const h = location.hash.replace(/^#/, '') as Tab;
    return ALL_TABS.includes(h) ? h : 'dashboard';
  }, [location.hash]);

  const goToTab = (t: Tab) => {
    // replace: true so back button doesn't replay every tab click.
    navigate({ hash: t, search: location.search }, { replace: true });
  };

  const signedInEmail = session?.user?.email ?? null;
  // CELL (M28): on the cell the role shown is the cell's answer (admitted = corporate_admin); off it, the operator row's.
  const operatorRole = isCell() ? (cellAdmitted ? 'corporate_admin' : null) : (operator?.role ?? null);
  const isCorporateAdmin = operatorRole === 'corporate_admin';

  // Gating banner if a non-admin somehow lands here. RLS will deny their
  // queries anyway, but we render a friendly message instead of a stack
  // of empty results.
  // Gate fix 2026-07-11: a plain Pro (operator === null) previously
  // fell through and rendered the empty portal shell. RLS kept the
  // data empty, but they shouldn't see admin chrome at all.
  // CELL (M28): fail closed — refused until the cell has admitted this person (asking, refused or unreachable all refuse).
  const refused = isCell()
    ? cellAdmitted !== true
    : operator === null || (operator && operator.role !== 'corporate_admin');
  if (refused) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-12">
        <div className="bg-white border border-image-border rounded-xl p-8 text-center">
          <h1 className="text-xl font-display font-bold text-image-black mb-2">
            Quality &amp; Operations Portal
          </h1>
          <p className="text-image-slate text-sm">
            This area is restricted to corporate administrators.
          </p>
          <p className="text-image-slate text-xs mt-3 font-mono">
            Signed in as {signedInEmail ?? '?'} (role: {operatorRole ?? (isCell() && cellAdmitted === null ? 'checking…' : 'unknown')})
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <header className="flex items-center justify-between border-b border-image-border pb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-display font-bold text-image-black tracking-tight">
            Quality &amp; Operations
          </h1>
          <p className="text-xs text-image-slate uppercase tracking-wider mt-0.5">
            Test discipline · Defect tracking · DD artifacts
          </p>
        </div>
        <div className="flex flex-col items-end gap-1">
          <div className={
            'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded ' +
            (isCorporateAdmin
              ? 'bg-emerald-100 text-emerald-800'
              : 'bg-amber-100 text-amber-800')
          }>
            {isCorporateAdmin ? '✓ corporate_admin' : (operatorRole ?? 'loading…')}
          </div>
          <div className="text-[11px] text-image-slate font-mono">
            {signedInEmail ?? 'pkntops.com/admin/qa'}
          </div>
        </div>
      </header>

      <Glossary />

      {/* Tab nav */}
      <nav className="flex gap-1 border-b border-image-border -mb-px overflow-x-auto">
        {(['dashboard', 'cases', 'runs', 'defects', 'builds', 'operations', 'artifacts'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => goToTab(t)}
            className={
              'px-4 py-2 text-sm font-semibold border-b-2 transition-colors ' +
              (tab === t
                ? 'border-image-pink text-image-black'
                : 'border-transparent text-image-slate hover:text-image-black')
            }
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </nav>

      {tab === 'dashboard'  && <DashboardTab />}
      {tab === 'cases'      && <CasesTab />}
      {tab === 'runs'       && <RunsTab />}
      {tab === 'defects'    && (isCell() ? <NotOnAws what="Defects" /> : <DefectsTab />)}
      {tab === 'builds'     && (isCell() ? <NotOnAws what="Build releases" /> : <BuildsTab />)}
      {tab === 'operations' && <OperationsTab />}
      {tab === 'artifacts'  && (isCell() ? <NotOnAws what="DD artifacts" /> : <ArtifactsTab />)}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Dashboard
// ─────────────────────────────────────────────────────────────────────

function DashboardTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['qa', 'dashboard'],
    queryFn: getDashboardSummary,
  });

  // Pull the full case list so each acceptance-gate ID can render its
  // human-readable title in a tooltip. Cached across tabs so it's free
  // once the Cases tab has loaded too.
  const { data: cases } = useQuery({
    queryKey: ['qa', 'cases'],
    queryFn: listTestCases,
  });
  const caseLookup = useMemo(() => {
    const m = new Map<string, TestCase>();
    (cases ?? []).forEach(c => m.set(c.id, c));
    return m;
  }, [cases]);

  if (isLoading || !data) return <p className="text-sm text-image-slate">Loading…</p>;

  const passPct = data.latestRunPassRate != null
    ? Math.round(data.latestRunPassRate * 100)
    : null;

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Test cases" value={data.totalCases} sub={`${data.p0Cases} P0 · ${data.p1Cases} P1`} />
        <Kpi label="Automatable" value={data.automatableCases} sub={`${Math.round(100 * data.automatableCases / Math.max(1, data.totalCases))}% of total`} />
        <Kpi label="Test runs" value={data.totalRuns} sub={data.latestRunAt ? `last ${relativeTime(data.latestRunAt)}` : 'none yet'} />
        <Kpi
          label="Latest pass rate"
          value={passPct != null ? `${passPct}%` : '—'}
          sub={passPct == null ? 'no results yet' : ''}
          tone={passPct == null ? undefined : passPct >= 95 ? 'green' : passPct >= 80 ? 'amber' : 'red'}
        />
      </div>

      {data.defectsNotOnAws ? (
        // CELL (M28): defects and DD artifacts are not on AWS (Cora 2026-10-29); say so rather than show zeros
        <p className="text-[12px] text-image-slate bg-white border border-image-border rounded-xl p-4">{QA_NOT_ON_AWS}</p>
      ) : (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Open defects" value={data.openDefects} sub={`${data.criticalOpenDefects} critical`} tone={data.criticalOpenDefects > 0 ? 'red' : data.openDefects > 0 ? 'amber' : 'green'} />
        <Kpi label="Defects fixed" value={data.defectsFixedAllTime} sub="all-time" />
        <Kpi label="Avg time-to-fix" value={data.averageTimeToFixDays != null ? `${data.averageTimeToFixDays.toFixed(1)}d` : '—'} sub={data.averageTimeToFixDays == null ? 'no fixes yet' : ''} />
        <Kpi label="DD artifacts" value={data.artifactsCount} sub="curated" />
      </div>
      )}

      <RunHistoryChart />

      <div className="bg-white border border-image-border rounded-xl p-5">
        <h2 className="text-sm font-bold uppercase tracking-wider text-image-black mb-1">
          Acceptance gates
        </h2>
        <p className="text-[11px] text-image-slate mb-3">
          Hover any test-case ID to see its title. Click it to open the case in the Cases tab.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <Gate title="Security baseline"          desc="AUTH-01 → AUTH-14, ERR-04, ERR-05, FLOW-08"  caseLookup={caseLookup} />
          <Gate title="Persistence guarantee"      desc="HOME-09, SET-12, SET-27/28, FLOW-06"           caseLookup={caseLookup} />
          <Gate title="Kira honesty"               desc="KIRA-12, KIRA-22, SET-17/18; every receipt logs" caseLookup={caseLookup} />
          <Gate title="Operational core"           desc="All P0 in B/D/E.1/E.4; FLOW-02; FLOW-04"       caseLookup={caseLookup} />
          <Gate title="Multi-location correctness" desc="FLOW-01, FLOW-08, OPS-O-02"                    caseLookup={caseLookup} />
          <Gate title="Telemetry + observability"  desc="Kira Writes inspector live; audit script all-green" caseLookup={caseLookup} />
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Run history chart (last N runs as horizontal bars)
// ─────────────────────────────────────────────────────────────────────

function RunHistoryChart() {
  const { data, isLoading } = useQuery({
    queryKey: ['qa', 'run-history'],
    queryFn: () => getRecentRunsPassRates(20),
  });

  if (isLoading) return null;
  if (!data || data.length === 0) {
    return (
      <div className="bg-white border border-image-border rounded-xl p-5">
        <h2 className="text-sm font-bold uppercase tracking-wider text-image-black mb-2">
          Run history
        </h2>
        <p className="text-xs text-image-slate">
          No runs recorded yet. Start your first run from the <strong>Runs</strong> tab.
        </p>
      </div>
    );
  }

  // Reverse so oldest is on the left, newest on the right (timeline reading order).
  const ordered = [...data].reverse();

  return (
    <div className="bg-white border border-image-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-image-black">
          Run history · last {data.length}
        </h2>
        <div className="flex items-center gap-3 text-[10px] text-image-slate uppercase tracking-wider">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Pass</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" /> Fail</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Block</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-300" /> Skip</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {ordered.map(r => (
          <div key={r.run_id} className="flex items-center gap-2 text-[11px]">
            <div className="w-44 truncate font-mono text-image-slate" title={r.run_id}>
              {r.is_acceptance && <span className="text-amber-700 font-bold">★ </span>}
              {r.run_id}
            </div>
            <div className="flex-1 h-5 rounded overflow-hidden bg-slate-100 flex">
              {r.total === 0 ? (
                <div className="flex-1 grid place-items-center text-[10px] text-slate-400">
                  no results
                </div>
              ) : (
                <>
                  {r.pass    > 0 && <div style={{ width: `${100 * r.pass / r.total}%` }}    className="bg-emerald-500" title={`${r.pass} pass`} />}
                  {r.fail    > 0 && <div style={{ width: `${100 * r.fail / r.total}%` }}    className="bg-red-500"     title={`${r.fail} fail`} />}
                  {r.blocked > 0 && <div style={{ width: `${100 * r.blocked / r.total}%` }} className="bg-amber-500"   title={`${r.blocked} blocked`} />}
                  {r.skip    > 0 && <div style={{ width: `${100 * r.skip / r.total}%` }}    className="bg-slate-300"   title={`${r.skip} skip`} />}
                </>
              )}
            </div>
            <div className="w-14 text-right font-mono text-image-slate">
              {r.total > 0 ? `${Math.round(100 * r.pct)}%` : '—'}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone }: {
  label: string; value: string | number; sub?: string;
  tone?: 'green' | 'amber' | 'red';
}) {
  const toneCls = {
    green: 'text-emerald-600',
    amber: 'text-amber-600',
    red: 'text-red-600',
  }[tone ?? 'green'] ?? 'text-image-black';
  return (
    <div className="bg-white border border-image-border rounded-xl p-4">
      <div className="text-[10px] uppercase tracking-wider text-image-slate font-bold">{label}</div>
      <div className={'text-2xl font-display font-bold mt-1 ' + (tone ? toneCls : 'text-image-black')}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-image-slate mt-0.5">{sub}</div>}
    </div>
  );
}

function Gate({ title, desc, caseLookup }: {
  title: string;
  desc: string;
  caseLookup: Map<string, TestCase>;
}) {
  const tokens = useMemo(() => parseGateDesc(desc), [desc]);
  return (
    <div className="border border-image-border rounded-lg p-3 bg-image-fog/40">
      <div className="text-sm font-semibold text-image-black">{title}</div>
      <div className="text-[11px] text-image-slate mt-0.5">
        {tokens.map((tok, i) => {
          if (typeof tok === 'string') return <span key={i}>{tok}</span>;
          const tc = caseLookup.get(tok.id);
          const tooltip = tc ? `${tc.id}: ${tc.title}` : `${tok.id} (test case not found)`;
          if (!tc) {
            // Unknown ID — render as plain mono text so a typo is visible
            // without becoming a broken link.
            return (
              <span
                key={i}
                title={tooltip}
                className="font-mono text-image-slate/80"
              >
                {tok.id}
              </span>
            );
          }
          return (
            <Link
              key={i}
              to={`/admin/qa?q=${encodeURIComponent(tok.id)}#cases`}
              title={tooltip}
              className="font-mono text-image-pink hover:underline"
            >
              {tok.id}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

// Parse a gate description string into a stream of tokens. Each test-case
// ID becomes a clickable chip; everything else is plain text. Handles:
//   - simple IDs:        AUTH-15
//   - sub-section IDs:   OPS-O-02, DASH-D-01
//   - paired shorthand:  SET-27/28  → SET-27 + / + SET-28
function parseGateDesc(desc: string): Array<string | { id: string }> {
  const re = /([A-Z]+(?:-[A-Z]{1,3})?)-(\d+)(?:\/(\d+))?/g;
  const out: Array<string | { id: string }> = [];
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(desc))) {
    if (m.index > last) out.push(desc.slice(last, m.index));
    const prefix = m[1];
    const numA = m[2];
    out.push({ id: `${prefix}-${numA}` });
    if (m[3]) {
      out.push('/');
      const numB = m[3].padStart(numA.length, '0');
      out.push({ id: `${prefix}-${numB}` });
    }
    last = re.lastIndex;
  }
  if (last < desc.length) out.push(desc.slice(last));
  return out;
}

// ─────────────────────────────────────────────────────────────────────
// Glossary — plain-English key for non-technical buyer-side readers.
// Collapsed by default; the jargon stays for technical reviewers but
// anyone unfamiliar can expand for context.
// ─────────────────────────────────────────────────────────────────────

function Glossary() {
  const terms: Array<[string, string]> = [
    ['DD',                'Due Diligence — a buyer’s tech & ops review before purchase.'],
    ['Test case (TC)',    'A single repeatable scenario with a stable ID like AUTH-15. Either passes or fails.'],
    ['Acceptance run',    'A complete pass through critical test cases that gates a release.'],
    ['Pass rate',         'Percentage of cases that passed in a run; closer to 100% = healthier.'],
    ['Defect',            'A bug that’s been logged, ranked, and assigned to be fixed.'],
    ['P0 / P1 / P2',      'Priority. P0 blocks launch, P1 is important, P2 is nice-to-have.'],
    ['Section',           'High-level grouping of test cases (e.g. Section A = Auth, B = Home tab).'],
    ['XCUITest',          'Apple’s automated UI testing framework — drives the iOS app like a person would.'],
    ['CI / GHA',          'Continuous Integration / GitHub Actions — runs tests automatically on every code change.'],
    ['Edge Function',     'A small serverless API endpoint hosted at Supabase.'],
    ['RLS',               'Row-Level Security — database rules that filter what each user can see.'],
    ['Multi-tenancy',     'One codebase serving multiple separate locations without data leaks between them.'],
    ['iOS',               'The Apple iPhone/iPad app side of the system.'],
    ['Hub',               'The web admin dashboard at pkntops.com.'],
    ['Append-only',       'A data table where rows can be added but never edited or deleted (audit trail).'],
    ['TestFlight',        'Apple’s beta-build distribution platform.'],
    ['Magic link / OTP',  'Sign in via emailed one-time code, no password.'],
    ['Audit script',      'Automated server-side health check that sweeps for problems on a schedule.'],
    ['Supabase',          'The cloud Postgres database + auth + Edge Functions provider this app runs on.'],
    ['n8n',               'Visual workflow-automation tool (legacy); some background jobs still run there.'],
    ['GHL',               'GoHighLevel — the marketing/CRM platform used for lead intake + pipeline.'],
  ];

  return (
    <details className="bg-image-fog/40 border border-image-border rounded-xl">
      <summary className="px-4 py-2 text-[11px] uppercase tracking-wider font-bold text-image-black cursor-pointer hover:bg-image-fog/70 rounded-xl">
        Plain-English glossary <span className="text-image-slate font-normal normal-case tracking-normal">— click to expand</span>
      </summary>
      <div className="px-4 pb-4 pt-1 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
        {terms.map(([t, d]) => (
          <div key={t} className="leading-snug">
            <span className="font-bold text-image-black">{t}</span>
            <span className="text-image-slate"> — {d}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Cases
// ─────────────────────────────────────────────────────────────────────

function CasesTab() {
  const location = useLocation();
  const navigate = useNavigate();
  const { data: cases, isLoading } = useQuery({
    queryKey: ['qa', 'cases'],
    queryFn: listTestCases,
  });

  // Initial search filter pre-filled from ?q= so the Dashboard's
  // gate-card links land here with the right TC already filtered.
  const initialQ = useMemo(() => {
    return new URLSearchParams(location.search).get('q') ?? '';
  }, [location.search]);

  const [section, setSection] = useState('');
  const [priority, setPriority] = useState<Priority | ''>('');
  const [type, setType] = useState<TestType | ''>('');
  const [search, setSearch] = useState(initialQ);

  // If the URL ?q= changes (e.g. user clicks a different gate ID
  // while already on the Cases tab), update the search box.
  useEffect(() => {
    if (initialQ !== search) setSearch(initialQ);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQ]);

  const clearQParam = () => {
    if (location.search) {
      navigate({ search: '', hash: location.hash || '#cases' }, { replace: true });
    }
  };

  const filtered = useMemo(() => {
    return (cases ?? []).filter(c => {
      if (section && c.section !== section) return false;
      if (priority && c.priority !== priority) return false;
      if (type && c.test_type !== type) return false;
      if (search) {
        const s = search.toLowerCase();
        if (!c.title.toLowerCase().includes(s) && !c.id.toLowerCase().includes(s)) return false;
      }
      return true;
    });
  }, [cases, section, priority, type, search]);

  // Row expansion: clicking a row reveals preconditions / steps / expected
  // / validates_commit / harness_action inline. When the filter narrows
  // to exactly one row (typically because a Dashboard gate-link landed
  // here via ?q=ID), auto-expand that row so you see the full case
  // immediately without an extra click.
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const lastAutoExpand = useMemo(() => {
    return filtered.length === 1 ? filtered[0].id : null;
  }, [filtered]);
  useEffect(() => {
    if (lastAutoExpand) {
      setExpanded(prev => {
        if (prev.has(lastAutoExpand)) return prev;
        const next = new Set(prev);
        next.add(lastAutoExpand);
        return next;
      });
    }
  }, [lastAutoExpand]);

  const toggleRow = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const expandAll = () => setExpanded(new Set(filtered.map(c => c.id)));
  const collapseAll = () => setExpanded(new Set());

  const sectionsList = Array.from(new Set((cases ?? []).map(c => c.section))).sort();

  if (isLoading) return <p className="text-sm text-image-slate">Loading cases…</p>;

  return (
    <div className="space-y-3">
      <SectionHeatmap />

      <div className="flex flex-wrap gap-2 items-center bg-white border border-image-border rounded-xl px-3 py-2">
        <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Filters</span>
        <Select value={section} onChange={setSection} options={[['', 'All sections'], ...sectionsList.map(s => [s, `Section ${s}`] as [string, string])]} />
        <Select value={priority} onChange={(v) => setPriority(v as Priority | '')} options={[['', 'All priorities'], ['P0', 'P0'], ['P1', 'P1'], ['P2', 'P2'], ['DEV', 'DEV']]} />
        <Select value={type} onChange={(v) => setType(v as TestType | '')} options={[['', 'All types'], ['M', 'Manual'], ['A', 'Automatable'], ['H', 'Hybrid'], ['U', 'Unit']]} />
        <input
          value={search}
          onChange={e => { setSearch(e.target.value); clearQParam(); }}
          placeholder="Search title / ID…"
          className="px-2 py-1 text-sm border border-image-border rounded flex-1 min-w-[180px]"
        />
        {search && (
          <button
            onClick={() => { setSearch(''); clearQParam(); }}
            className="text-[11px] text-image-pink hover:underline px-1"
            title="Clear search filter"
          >
            clear
          </button>
        )}
        <span className="text-[11px] text-image-slate">{filtered.length} of {cases?.length ?? 0}</span>
        <span className="text-image-border">·</span>
        <button
          onClick={expandAll}
          className="text-[11px] text-image-slate hover:text-image-pink"
          title="Expand all visible rows"
        >
          expand all
        </button>
        <button
          onClick={collapseAll}
          className="text-[11px] text-image-slate hover:text-image-pink"
          title="Collapse all expanded rows"
        >
          collapse all
        </button>
      </div>

      <div className="bg-white border border-image-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-image-fog/60 text-[10px] uppercase tracking-wider text-image-slate">
              <th className="w-6 px-2 py-2"></th>
              <th className="text-left px-3 py-2">ID</th>
              <th className="text-left px-3 py-2">Title</th>
              <th className="text-left px-3 py-2">Section</th>
              <th className="text-left px-3 py-2 w-12">Type</th>
              <th className="text-left px-3 py-2 w-12">Pri</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => {
              const isOpen = expanded.has(c.id);
              return (
                <CaseRow
                  key={c.id}
                  c={c}
                  isOpen={isOpen}
                  onToggle={() => toggleRow(c.id)}
                />
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-sm text-image-slate">
                  No test cases match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CaseRow({ c, isOpen, onToggle }: {
  c: TestCase;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <tr
        className={
          'border-t border-image-border cursor-pointer ' +
          (isOpen ? 'bg-image-pink/5' : 'hover:bg-image-fog/40')
        }
        onClick={onToggle}
      >
        <td className="w-6 px-2 py-2 text-center text-image-slate select-none">
          <span className={'inline-block transition-transform ' + (isOpen ? 'rotate-90' : '')}>›</span>
        </td>
        <td className="px-3 py-2 font-mono text-[11px] text-image-pink font-bold">{c.id}</td>
        <td className="px-3 py-2 font-medium text-image-black">
          {c.title}
          {c.notes && !isOpen && (
            <div className="text-[11px] text-image-slate italic mt-0.5">↳ {c.notes}</div>
          )}
        </td>
        <td className="px-3 py-2 text-[11px] text-image-slate">
          {c.subsection ?? `Section ${c.section}`}
        </td>
        <td className="px-3 py-2"><Pill kind="type" value={c.test_type} /></td>
        <td className="px-3 py-2"><Pill kind="pri" value={c.priority} /></td>
      </tr>
      {isOpen && (
        <tr className="bg-image-pink/5 border-t border-image-pink/20">
          <td></td>
          <td colSpan={5} className="px-3 py-3">
            <CaseDetail c={c} />
          </td>
        </tr>
      )}
    </>
  );
}

function CaseDetail({ c }: { c: TestCase }) {
  const harnessJson = c.harness_action
    ? JSON.stringify(c.harness_action, null, 2)
    : null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-3 text-[12px] leading-snug">
      <DetailField label="Preconditions" value={c.preconditions} />
      <DetailField label="Steps"         value={c.steps} />
      <DetailField label="Expected"      value={c.expected} />
      {c.persistence_target && (
        <DetailField
          label="Lands in (verify after running)"
          className="md:col-span-3"
          render={
            <div className="font-mono text-[11px] text-image-black bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5 inline-block">
              {c.persistence_target}
            </div>
          }
        />
      )}
      {c.notes && <DetailField label="Notes" value={c.notes} className="md:col-span-2" />}
      {c.validates_commit && (
        <DetailField
          label="Validates commit"
          className="md:col-span-1"
          render={
            <a
              href={`https://github.com/plewis-pknt/pknt-ops-ios/commit/${c.validates_commit}`}
              target="_blank"
              rel="noreferrer"
              className="font-mono text-image-pink hover:underline"
            >
              {c.validates_commit}
            </a>
          }
        />
      )}
      {harnessJson && (
        <DetailField
          label="Harness action"
          className="md:col-span-3"
          render={
            <pre className="font-mono text-[11px] bg-white border border-image-border rounded p-2 overflow-x-auto">{harnessJson}</pre>
          }
        />
      )}
      <div className="md:col-span-3 flex flex-wrap gap-3 pt-2 border-t border-image-pink/20 text-[11px]">
        <span className="text-image-slate">
          <span className="font-bold text-image-black">Section</span> {c.subsection ?? c.section}
        </span>
        <span className="text-image-slate">
          <span className="font-bold text-image-black">Priority</span> {c.priority}
        </span>
        <span className="text-image-slate">
          <span className="font-bold text-image-black">Type</span> {c.test_type === 'A' ? 'Automatable' : c.test_type === 'M' ? 'Manual' : c.test_type === 'H' ? 'Hybrid' : 'Unit'}
        </span>
        <CopyCaseLink caseId={c.id} />
      </div>
    </div>
  );
}

function DetailField({ label, value, render, className }: {
  label: string;
  value?: string | null;
  render?: React.ReactNode;
  className?: string;
}) {
  if (!render && !(value && value.trim().length > 0)) return null;
  return (
    <div className={className}>
      <div className="text-[10px] uppercase tracking-wider font-bold text-image-slate mb-0.5">
        {label}
      </div>
      <div className="text-image-black">
        {render ?? value}
      </div>
    </div>
  );
}

function Select({ value, onChange, options, disabled }: {
  value: string; onChange: (v: string) => void; options: [string, string][]; disabled?: boolean;
}) {
  return (
    <select
      value={value}
      disabled={disabled}
      onChange={e => onChange(e.target.value)}
      className="px-2 py-1 text-sm border border-image-border rounded bg-white"
    >
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}

function Pill({ kind, value }: { kind: 'type' | 'pri' | 'status' | 'severity'; value: string }) {
  const map: Record<string, string> = {
    'type:M': 'bg-violet-50 text-violet-700',
    'type:A': 'bg-emerald-50 text-emerald-700',
    'type:H': 'bg-amber-50 text-amber-700',
    'type:U': 'bg-blue-50 text-blue-700',
    'pri:P0': 'bg-red-50 text-red-700',
    'pri:P1': 'bg-amber-50 text-amber-700',
    'pri:P2': 'bg-slate-100 text-slate-700',
    'pri:DEV': 'bg-blue-50 text-blue-700',
    'status:pass': 'bg-emerald-50 text-emerald-700',
    'status:fail': 'bg-red-50 text-red-700',
    'status:blocked': 'bg-amber-50 text-amber-700',
    'status:skip': 'bg-slate-100 text-slate-500',
    'severity:critical': 'bg-red-100 text-red-800',
    'severity:high': 'bg-orange-50 text-orange-700',
    'severity:medium': 'bg-amber-50 text-amber-700',
    'severity:low': 'bg-slate-100 text-slate-700',
  };
  const cls = map[`${kind}:${value}`] ?? 'bg-slate-100 text-slate-700';
  return (
    <span className={`inline-block px-1.5 py-0.5 text-[10px] font-mono font-bold rounded ${cls}`}>
      {value}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Runs
// ─────────────────────────────────────────────────────────────────────

export function RunsTab() {
  const qc = useQueryClient();
  const { data: runs, isLoading } = useQuery({
    queryKey: ['qa', 'runs'],
    queryFn: () => listTestRuns(50),
  });

  const [executingRun, setExecutingRun] = useState<TestRun | null>(null);

  const startRun = useMutation({
    mutationFn: (input: Parameters<typeof createTestRun>[0]) => createTestRun(input),
    onSuccess: (newRun) => {
      qc.invalidateQueries({ queryKey: ['qa', 'runs'] });
      setExecutingRun(newRun);
    },
  });

  const [showNewRun, setShowNewRun] = useState(false);
  const [newRunBuild, setNewRunBuild] = useState('');
  const [newRunEnv, setNewRunEnv] = useState<RunEnvironment>('DEV-Sim');
  const [newRunIsAcceptance, setNewRunIsAcceptance] = useState(false);

  const { session } = useSession();
  const tester = session?.user?.email ?? null;

  if (isLoading) return <p className="text-sm text-image-slate">Loading runs…</p>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between bg-white border border-image-border rounded-xl px-3 py-2">
        <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">
          {runs?.length ?? 0} runs · most recent first
        </span>
        <button
          onClick={() => setShowNewRun(true)}
          className="px-3 py-1.5 text-sm font-semibold bg-image-black text-white rounded hover:bg-image-pink transition-colors"
        >
          + Start new run
        </button>
      </div>

      {showNewRun && (
        <div className="bg-white border-2 border-image-pink rounded-xl p-4 space-y-3">
          <h3 className="text-sm font-bold text-image-black">New test run</h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Build</span>
              <input
                value={newRunBuild}
                onChange={e => setNewRunBuild(e.target.value)}
                placeholder="1.0.47 or commit short SHA"
                className="px-2 py-1 border border-image-border rounded"
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Environment</span>
              <Select
                value={newRunEnv}
                onChange={(v) => setNewRunEnv(v as RunEnvironment)}
                options={[['DEV-Sim', 'DEV-Sim'], ['DEV-Device', 'DEV-Device'], ['TestFlight', 'TestFlight'], ['Production', 'Production'], ['Staging', 'Staging'], ['GitHub Actions', 'GitHub Actions'], ['CI', 'CI']]}
              />
            </label>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={newRunIsAcceptance}
              onChange={e => setNewRunIsAcceptance(e.target.checked)}
            />
            <span>Acceptance run (gates a release)</span>
          </label>
          <MutationError m={startRun} what="The new run" />
          <div className="flex gap-2">
            <button
              onClick={() => {
                // HUB-061: the draft closes and clears only once the run exists; a refusal keeps it.
                startRun.mutate({
                  build_version: newRunBuild || null,
                  environment: newRunEnv,
                  tester_email: tester,
                  is_acceptance_run: newRunIsAcceptance,
                }, {
                  onSuccess: () => {
                    setShowNewRun(false);
                    setNewRunBuild('');
                    setNewRunIsAcceptance(false);
                  },
                });
              }}
              disabled={startRun.isPending}
              className="px-4 py-1.5 text-sm font-semibold bg-image-black text-white rounded hover:bg-image-pink disabled:opacity-50"
            >
              {startRun.isPending ? 'Starting…' : 'Start'}
            </button>
            <button
              onClick={() => setShowNewRun(false)}
              className="px-4 py-1.5 text-sm font-semibold border border-image-border rounded text-image-slate hover:text-image-black"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-image-border rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-image-fog/60 text-[10px] uppercase tracking-wider text-image-slate">
              <th className="text-left px-3 py-2">Run ID</th>
              <th className="text-left px-3 py-2">Build</th>
              <th className="text-left px-3 py-2">Env</th>
              <th className="text-left px-3 py-2">Tester</th>
              <th className="text-left px-3 py-2">Started</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {(runs ?? []).map(r => (
              <tr key={r.id} className="border-t border-image-border hover:bg-image-fog/40">
                <td className="px-3 py-2 font-mono text-[11px] text-image-pink font-bold">
                  {r.run_id}
                  {r.is_acceptance_run && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-700 font-bold">★ accept</span>}
                </td>
                <td className="px-3 py-2 text-[11px] font-mono">{r.build_version ?? '—'}</td>
                <td className="px-3 py-2 text-[11px]">{r.environment ?? '—'}</td>
                <td className="px-3 py-2 text-[11px] text-image-slate">{r.tester_email ?? '—'}</td>
                <td className="px-3 py-2 text-[11px] text-image-slate">{relativeTime(r.started_at)}</td>
                <td className="px-3 py-2 text-[11px]">
                  {r.completed_at
                    ? <span className="text-emerald-700 font-semibold">Completed</span>
                    : <span className="text-amber-700 font-semibold">In progress</span>}
                </td>
                <td className="px-3 py-2">
                  <button
                    onClick={() => setExecutingRun(r)}
                    className="px-2 py-1 text-[11px] font-semibold border border-image-border rounded hover:bg-image-fog"
                  >
                    {r.completed_at ? 'View' : 'Resume'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {executingRun && (
        <RunExecutionSheet
          run={executingRun}
          onClose={() => {
            setExecutingRun(null);
            qc.invalidateQueries({ queryKey: ['qa', 'runs'] });
            qc.invalidateQueries({ queryKey: ['qa', 'dashboard'] });
          }}
        />
      )}
    </div>
  );
}

export function RunExecutionSheet({ run, onClose }: { run: TestRun; onClose: () => void }) {
  const qc = useQueryClient();
  const { data: cases } = useQuery({
    queryKey: ['qa', 'cases'],
    queryFn: listTestCases,
  });
  const { data: results } = useQuery({
    queryKey: ['qa', 'results', run.id],
    queryFn: () => listResultsForRun(run.id),
  });

  const resultsByCase = useMemo(() => {
    const m: Record<string, TestResult> = {};
    (results ?? []).forEach(r => { m[r.test_case_id] = r; });
    return m;
  }, [results]);

  const [filter, setFilter] = useState<'all' | 'untested' | 'fail' | 'p0'>('all');
  const [search, setSearch] = useState('');
  const { session } = useSession();

  const visible = useMemo(() => {
    return (cases ?? []).filter(c => {
      if (filter === 'untested' && resultsByCase[c.id]) return false;
      if (filter === 'fail' && resultsByCase[c.id]?.status !== 'fail') return false;
      if (filter === 'p0' && c.priority !== 'P0') return false;
      if (search && !c.title.toLowerCase().includes(search.toLowerCase()) && !c.id.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [cases, resultsByCase, filter, search]);

  const counts = useMemo(() => {
    const r = results ?? [];
    return {
      pass: r.filter(x => x.status === 'pass').length,
      fail: r.filter(x => x.status === 'fail').length,
      blocked: r.filter(x => x.status === 'blocked').length,
      skip: r.filter(x => x.status === 'skip').length,
      total: cases?.length ?? 0,
    };
  }, [results, cases]);

  // HUB-061 (partly round): ONE mutation serves every row, and its isError/variables describe only the LATEST call —
  // so row A's failure vanished the moment row B was pressed. Each row now keeps its own in-flight flag and its own
  // failure (useRowWrites), fed by the mutation-level callbacks, which fire for EVERY call with that call's variables.
  const recordRows = useRowWrites();
  const record = useMutation({
    mutationFn: recordResult,
    onMutate: (v) => recordRows.start(v.test_case_id),
    onError: (e, v) => recordRows.fail(v.test_case_id, e),
    onSuccess: (_d, v) => {
      recordRows.ok(v.test_case_id);
      qc.invalidateQueries({ queryKey: ['qa', 'results', run.id] });
    },
    onSettled: (_d, _e, v) => recordRows.end(v.test_case_id),
  });

  const complete = useMutation({
    mutationFn: () => completeTestRun(run.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa', 'runs'] });
      onClose();
    },
  });

  const [defectFor, setDefectFor] = useState<TestCase | null>(null);

  return (
    <div className="fixed inset-0 z-40 bg-image-black/40 flex items-end md:items-center justify-center p-2 md:p-6">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[92vh] flex flex-col">
        <header className="flex items-center justify-between border-b border-image-border px-5 py-3">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Run execution</div>
            <div className="font-mono text-sm text-image-pink font-bold">
              {run.run_id}
              {run.build_version && <span className="ml-2 text-image-slate font-normal">· build {run.build_version}</span>}
              {run.environment && <span className="ml-2 text-image-slate font-normal">· {run.environment}</span>}
            </div>
          </div>
          <div className="flex gap-2">
            {!run.completed_at && (
              <button
                onClick={() => complete.mutate()}
                className="px-3 py-1.5 text-sm font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700"
                disabled={complete.isPending}
              >
                Complete run
              </button>
            )}
            <button onClick={onClose} className="px-3 py-1.5 text-sm font-semibold border border-image-border rounded">Close</button>
          </div>
        </header>

        <div className="px-5 py-3 border-b border-image-border bg-image-fog/40 flex flex-wrap items-center gap-3 text-xs">
          <span className="font-bold text-image-slate uppercase tracking-wider">Progress</span>
          <span><span className="font-bold text-emerald-700">{counts.pass}</span> pass</span>
          <span><span className="font-bold text-red-700">{counts.fail}</span> fail</span>
          <span><span className="font-bold text-amber-700">{counts.blocked}</span> blocked</span>
          <span><span className="font-bold text-slate-500">{counts.skip}</span> skip</span>
          <span className="text-image-slate">·</span>
          <span>{counts.pass + counts.fail + counts.blocked + counts.skip} of {counts.total} recorded</span>
          <span className="flex-1" />
          <Select
            value={filter}
            onChange={(v) => setFilter(v as 'all' | 'untested' | 'fail' | 'p0')}
            options={[['all', 'All'], ['untested', 'Untested only'], ['fail', 'Failed only'], ['p0', 'P0 only']]}
          />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search…"
            className="px-2 py-1 text-sm border border-image-border rounded"
          />
        </div>

        {complete.isError && (
          <div className="px-5 py-2 border-b border-image-border space-y-1">
            <MutationError m={complete} what="Completing the run" />
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white shadow-sm">
              <tr className="bg-image-fog/60 text-[10px] uppercase tracking-wider text-image-slate">
                <th className="text-left px-3 py-2">ID</th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2 w-12">Pri</th>
                <th className="text-left px-3 py-2 w-[260px]">Status</th>
                <th className="text-left px-3 py-2 w-20"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map(c => {
                const r = resultsByCase[c.id];
                return (
                  <tr key={c.id} className="border-t border-image-border">
                    <td className="px-3 py-2 font-mono text-[11px] text-image-pink font-bold">{c.id}</td>
                    <td className="px-3 py-2">
                      <div className="font-medium text-image-black">{c.title}</div>
                      <div className="text-[11px] text-image-slate mt-0.5">{c.steps}</div>
                      <div className="text-[11px] text-image-slate mt-0.5"><span className="font-bold">Expected:</span> {c.expected}</div>
                      {r?.observed && <div className="text-[11px] text-red-700 mt-0.5"><span className="font-bold">Observed:</span> {r.observed}</div>}
                      <RowError detail={recordRows.error(c.id)} what={`The result for ${c.id}`} />
                    </td>
                    <td className="px-3 py-2"><Pill kind="pri" value={c.priority} /></td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(['pass', 'fail', 'blocked', 'skip'] as ResultStatus[]).map(s => (
                          <button
                            key={s}
                            onClick={() => record.mutate({
                              run_id: run.id,
                              test_case_id: c.id,
                              status: s,
                              recorded_by: session?.user?.email ?? null,
                              observed: s === 'fail' ? (window.prompt(`What did you observe for ${c.id}?`) ?? null) : null,
                            })}
                            // HUB-061: one result write per case at a time, so two quick taps cannot land out of order.
                            disabled={!!run.completed_at || recordRows.pending(c.id)}
                            className={
                              'px-2 py-0.5 text-[10px] font-mono font-bold rounded uppercase ' +
                              (r?.status === s
                                ? statusButtonActive(s)
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200')
                            }
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2">
                      {/* CELL (M28): defects are not on AWS (Cora 2026-10-29), so a failed case offers no File defect there */}
                      {r?.status === 'fail' && !isCell() && (
                        <button
                          onClick={() => setDefectFor(c)}
                          className="text-[10px] uppercase tracking-wider text-image-pink font-bold hover:underline"
                        >
                          File defect
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {defectFor && (
        <FileDefectModal
          testCase={defectFor}
          run={run}
          result={resultsByCase[defectFor.id] ?? null}
          onClose={() => setDefectFor(null)}
        />
      )}
    </div>
  );
}

function statusButtonActive(s: ResultStatus): string {
  return {
    pass: 'bg-emerald-600 text-white',
    fail: 'bg-red-600 text-white',
    blocked: 'bg-amber-600 text-white',
    skip: 'bg-slate-500 text-white',
  }[s];
}

// ─────────────────────────────────────────────────────────────────────
// File defect modal
// ─────────────────────────────────────────────────────────────────────

function FileDefectModal({
  testCase, run, result, onClose,
}: { testCase: TestCase; run: TestRun; result: TestResult | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [title, setTitle] = useState(`[${testCase.id}] ${testCase.title} — failed`);
  const [description, setDescription] = useState(result?.observed ?? '');
  const [severity, setSeverity] = useState<DefectSeverity>(testCase.priority === 'P0' ? 'critical' : 'medium');
  const { session } = useSession();

  const file = useMutation({
    mutationFn: () => fileDefect({
      title,
      description,
      severity,
      test_case_id: testCase.id,
      test_run_id: run.id,
      test_result_id: result?.id ?? null,
      reported_by: session?.user?.email ?? null,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['qa', 'defects'] });
      qc.invalidateQueries({ queryKey: ['qa', 'dashboard'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-image-black/60 grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-5 space-y-3">
        <h3 className="text-base font-bold text-image-black">File defect</h3>
        <div className="text-[11px] font-mono text-image-slate">
          From {testCase.id} · run {run.run_id}
        </div>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Title</span>
          <input
            value={title}
            onChange={e => setTitle(e.target.value)}
            className="w-full px-2 py-1 text-sm border border-image-border rounded mt-1"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Description / observed behavior</span>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={5}
            className="w-full px-2 py-1 text-sm border border-image-border rounded mt-1"
          />
        </label>
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Severity</span>
          <Select
            value={severity}
            onChange={(v) => setSeverity(v as DefectSeverity)}
            options={[['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']]}
          />
        </label>
        <MutationError m={file} what="The defect" />
        <div className="flex gap-2 justify-end pt-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm font-semibold border border-image-border rounded text-image-slate hover:text-image-black"
          >
            Cancel
          </button>
          <button
            onClick={() => file.mutate()}
            disabled={file.isPending || !title.trim()}
            className="px-3 py-1.5 text-sm font-semibold bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
          >
            File defect
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Defects
// ─────────────────────────────────────────────────────────────────────

export function DefectsTab() {
  const qc = useQueryClient();
  const { data: defects, isLoading } = useQuery({
    queryKey: ['qa', 'defects'],
    queryFn: listDefects,
  });
  const [statusFilter, setStatusFilter] = useState<DefectStatus | ''>('');
  const [severityFilter, setSeverityFilter] = useState<DefectSeverity | ''>('');

  const filtered = (defects ?? []).filter(d => {
    if (statusFilter && d.status !== statusFilter) return false;
    if (severityFilter && d.severity !== severityFilter) return false;
    return true;
  });

  // HUB-061 (partly round): per-defect in-flight and failure, as the run's rows above.
  const updateRows = useRowWrites();
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Defect> }) => updateDefect(id, patch),
    onMutate: (v) => updateRows.start(v.id),
    onError: (e, v) => updateRows.fail(v.id, e),
    onSuccess: (_d, v) => {
      updateRows.ok(v.id);
      qc.invalidateQueries({ queryKey: ['qa', 'defects'] });
      qc.invalidateQueries({ queryKey: ['qa', 'dashboard'] });
    },
    onSettled: (_d, _e, v) => updateRows.end(v.id),
  });

  if (isLoading) return <p className="text-sm text-image-slate">Loading defects…</p>;

  return (
    <div className="space-y-3">
      <DefectStatsBlock />

      <div className="flex flex-wrap gap-2 items-center bg-white border border-image-border rounded-xl px-3 py-2">
        <span className="text-[10px] uppercase tracking-wider text-image-slate font-bold">Filters</span>
        <Select
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as DefectStatus | '')}
          options={[['', 'All statuses'], ['open', 'Open'], ['investigating', 'Investigating'], ['fixed', 'Fixed'], ['verified', 'Verified'], ['closed', 'Closed'], ['wontfix', "Won't fix"]]}
        />
        <Select
          value={severityFilter}
          onChange={(v) => setSeverityFilter(v as DefectSeverity | '')}
          options={[['', 'All severities'], ['critical', 'Critical'], ['high', 'High'], ['medium', 'Medium'], ['low', 'Low']]}
        />
        <span className="text-[11px] text-image-slate ml-auto">{filtered.length} of {defects?.length ?? 0}</span>
      </div>

      {filtered.length === 0 ? (
        <div className="bg-white border border-image-border rounded-xl p-10 text-center">
          <div className="text-3xl mb-2">🟢</div>
          <div className="text-sm font-semibold text-image-black">No defects in scope</div>
          <div className="text-[11px] text-image-slate mt-1">File new defects from a failed test result during a run.</div>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(d => (
            <div key={d.id} className="bg-white border border-image-border rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="font-mono text-[11px] font-bold text-image-pink mt-0.5 min-w-[70px]">{d.defect_number}</div>
                <div className="flex-1">
                  <div className="font-semibold text-image-black">{d.title}</div>
                  {d.description && <div className="text-[12px] text-image-slate mt-1 whitespace-pre-wrap">{d.description}</div>}
                  <RowError detail={updateRows.error(d.id)} what={`The status change for ${d.defect_number}`} />
                  <div className="flex flex-wrap gap-2 mt-2 text-[11px] text-image-slate items-center">
                    <Pill kind="severity" value={d.severity} />
                    <span>·</span>
                    <span>Reported {relativeTime(d.reported_at)}{d.reported_by ? ` by ${d.reported_by}` : ''}</span>
                    {d.test_case_id && <><span>·</span><span>From <span className="font-mono font-bold text-image-pink">{d.test_case_id}</span></span></>}
                    {d.fix_commit_sha && <><span>·</span><span>Fixed in <code className="text-image-pink">{d.fix_commit_sha}</code></span></>}
                  </div>
                </div>
                <Select
                  value={d.status}
                  // HUB-061: one status write per defect at a time (out-of-order changes otherwise).
                  disabled={updateRows.pending(d.id)}
                  onChange={(v) => update.mutate({ id: d.id, patch: { status: v as DefectStatus } })}
                  options={[['open', 'Open'], ['investigating', 'Investigating'], ['fixed', 'Fixed'], ['verified', 'Verified'], ['closed', 'Closed'], ['wontfix', "Won't fix"]]}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Artifacts
// ─────────────────────────────────────────────────────────────────────

function ArtifactsTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['qa', 'artifacts'],
    queryFn: listDDArtifacts,
  });

  const grouped = useMemo(() => {
    const m: Record<string, DDArtifact[]> = {};
    (data ?? []).forEach(a => {
      (m[a.category] ||= []).push(a);
    });
    return m;
  }, [data]);

  const categoryLabels: Record<string, string> = {
    architecture: '🏗  Architecture',
    security: '🔒  Security',
    test: '🧪  Test',
    operations: '⚙️  Operations',
    compliance: '📋  Compliance',
    code: '💻  Code',
    incidents: '🚨  Incidents',
    ip: '⚖️  IP',
    people: '👥  People',
  };

  if (isLoading) return <p className="text-sm text-image-slate">Loading artifacts…</p>;

  return (
    <div className="space-y-4">
      <div className="bg-image-pink/10 border border-image-pink/40 rounded-xl p-4">
        <div className="text-[10px] uppercase tracking-wider text-image-pink font-bold mb-1">Due-diligence index</div>
        <div className="text-sm text-image-black">
          Curated catalog of every document, link, and live dashboard a technical reviewer would inspect during a code-base acquisition. {(data ?? []).length} artifacts across {Object.keys(grouped).length} categories.
        </div>
      </div>

      {Object.entries(categoryLabels).map(([cat, label]) => {
        const items = grouped[cat] ?? [];
        if (items.length === 0) return null;
        return (
          <section key={cat} className="bg-white border border-image-border rounded-xl p-4">
            <h3 className="text-sm font-bold text-image-black mb-3">{label}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {items.map(a => (
                <ArtifactCard key={a.id} artifact={a} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function ArtifactCard({ artifact }: { artifact: DDArtifact }) {
  const isExternal = artifact.url?.startsWith('http');
  const isInternalRoute = artifact.url?.startsWith('/admin');
  const isDocLink = artifact.url?.startsWith('/') && !isInternalRoute;
  const isSpecial = artifact.url?.startsWith('supabase://') || artifact.url?.startsWith('memory://');

  const inner = (
    <div className="border border-image-border rounded-lg p-3 hover:border-image-pink hover:bg-image-fog/40 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-semibold text-image-black">{artifact.title}</div>
        {artifact.badge && (
          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded">
            {artifact.badge}
          </span>
        )}
      </div>
      {artifact.description && (
        <div className="text-[12px] text-image-slate mt-1">{artifact.description}</div>
      )}
      {artifact.url && (
        <div className="text-[10px] font-mono text-image-pink/80 mt-2 truncate">{artifact.url}</div>
      )}
    </div>
  );

  if (isExternal) {
    return <a href={artifact.url!} target="_blank" rel="noreferrer">{inner}</a>;
  }
  if (isInternalRoute) {
    return <Link to={artifact.url!}>{inner}</Link>;
  }
  if (isDocLink) {
    // Link to a static HTML doc on pkntops.com — opens in same window
    return <a href={artifact.url!}>{inner}</a>;
  }
  if (isSpecial) {
    // supabase:// or memory:// — opaque references; render as info card
    return inner;
  }
  return inner;
}

// ─────────────────────────────────────────────────────────────────────
// Cases tab — section heatmap
// ─────────────────────────────────────────────────────────────────────

function SectionHeatmap() {
  const { data, isLoading } = useQuery({
    queryKey: ['qa', 'section-stats'],
    queryFn: getSectionStats,
  });
  if (isLoading || !data || data.length === 0) return null;

  const totalAcross = data.reduce((s, x) => s + x.total, 0);

  return (
    <div className="bg-white border border-image-border rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-bold uppercase tracking-wider text-image-black">
          Coverage by section
        </h2>
        <div className="flex items-center gap-3 text-[10px] text-image-slate uppercase tracking-wider">
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Pass</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-red-500" /> Fail</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> Block</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-slate-200" /> Untested</span>
        </div>
      </div>
      <div className="space-y-1.5">
        {data.map(s => (
          <div key={s.section} className="flex items-center gap-2 text-[11px]">
            <div className="w-10 font-mono text-image-black font-bold">§{s.section}</div>
            <div className="flex-1 h-5 rounded overflow-hidden bg-slate-100 flex">
              {s.passed   > 0 && <div style={{ width: `${100 * s.passed / s.total}%` }}   className="bg-emerald-500" title={`${s.passed} pass`} />}
              {s.failed   > 0 && <div style={{ width: `${100 * s.failed / s.total}%` }}   className="bg-red-500"     title={`${s.failed} fail`} />}
              {s.blocked  > 0 && <div style={{ width: `${100 * s.blocked / s.total}%` }}  className="bg-amber-500"   title={`${s.blocked} blocked`} />}
              {s.untested > 0 && <div style={{ width: `${100 * s.untested / s.total}%` }} className="bg-slate-200"   title={`${s.untested} untested`} />}
            </div>
            <div className="w-32 text-right font-mono text-image-slate">
              {s.total} cases · <span className="text-red-700">{s.p0} P0</span>
            </div>
          </div>
        ))}
        <div className="flex items-center gap-2 text-[10px] text-image-slate pt-1 border-t border-image-border mt-2">
          <div className="w-10 font-mono">total</div>
          <div className="flex-1 font-mono">{totalAcross} test cases across {data.length} sections</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Defects tab — stats block (status / severity / age buckets)
// ─────────────────────────────────────────────────────────────────────

function DefectStatsBlock() {
  const { data, isLoading } = useQuery({
    queryKey: ['qa', 'defect-stats'],
    queryFn: getDefectStats,
  });
  if (isLoading || !data) return null;

  const totalAll = Object.values(data.byStatus).reduce((a, b) => a + b, 0);
  if (totalAll === 0) return null;

  const statusEntries: [DefectStatus, string][] = [
    ['open', 'bg-red-500'],
    ['investigating', 'bg-amber-500'],
    ['fixed', 'bg-blue-500'],
    ['verified', 'bg-emerald-500'],
    ['closed', 'bg-emerald-600'],
    ['wontfix', 'bg-slate-400'],
  ];
  const severityEntries: [DefectSeverity, string][] = [
    ['critical', 'bg-red-600'],
    ['high', 'bg-orange-500'],
    ['medium', 'bg-amber-500'],
    ['low', 'bg-slate-400'],
  ];

  return (
    <div className="bg-white border border-image-border rounded-xl p-4 grid grid-cols-1 md:grid-cols-3 gap-5">
      <div>
        <h3 className="text-[10px] uppercase tracking-wider text-image-slate font-bold mb-2">Status</h3>
        <div className="h-4 rounded overflow-hidden bg-slate-100 flex">
          {statusEntries.map(([s, cls]) => {
            const count = data.byStatus[s] ?? 0;
            if (count === 0) return null;
            return <div key={s} style={{ width: `${100 * count / totalAll}%` }} className={cls} title={`${count} ${s}`} />;
          })}
        </div>
        <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
          {statusEntries.map(([s, cls]) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-sm ${cls}`} />
              <span className="text-image-slate">{s}</span>
              <span className="font-mono text-image-black ml-auto">{data.byStatus[s] ?? 0}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-[10px] uppercase tracking-wider text-image-slate font-bold mb-2">Severity</h3>
        <div className="h-4 rounded overflow-hidden bg-slate-100 flex">
          {severityEntries.map(([s, cls]) => {
            const count = data.bySeverity[s] ?? 0;
            if (count === 0) return null;
            return <div key={s} style={{ width: `${100 * count / totalAll}%` }} className={cls} title={`${count} ${s}`} />;
          })}
        </div>
        <div className="grid grid-cols-2 gap-1 mt-2 text-[11px]">
          {severityEntries.map(([s, cls]) => (
            <div key={s} className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-sm ${cls}`} />
              <span className="text-image-slate">{s}</span>
              <span className="font-mono text-image-black ml-auto">{data.bySeverity[s] ?? 0}</span>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h3 className="text-[10px] uppercase tracking-wider text-image-slate font-bold mb-2">Open age</h3>
        <div className="space-y-1.5">
          {data.ageBuckets.map(b => {
            const pct = data.totalOpen > 0 ? Math.round(100 * b.count / data.totalOpen) : 0;
            return (
              <div key={b.label} className="flex items-center gap-2 text-[11px]">
                <div className="w-12 font-mono text-image-slate">{b.label}</div>
                <div className="flex-1 h-3 rounded bg-slate-100 overflow-hidden">
                  <div className="h-full bg-red-500" style={{ width: `${pct}%` }} />
                </div>
                <div className="w-8 text-right font-mono text-image-black">{b.count}</div>
              </div>
            );
          })}
        </div>
        <div className="text-[10px] text-image-slate mt-2 italic">
          {data.totalOpen} open · ages bucketed
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Builds tab — release ledger
// ─────────────────────────────────────────────────────────────────────

function BuildsTab() {
  const { data: builds, isLoading } = useQuery({
    queryKey: ['qa', 'builds'],
    queryFn: listBuildReleases,
  });

  if (isLoading) return <p className="text-sm text-image-slate">Loading releases…</p>;

  if (!builds || builds.length === 0) {
    return (
      <div className="bg-white border border-image-border rounded-xl p-10 text-center">
        <div className="text-3xl mb-2">📦</div>
        <div className="text-sm font-semibold text-image-black">No releases recorded yet</div>
        <div className="text-[12px] text-image-slate mt-1 max-w-md mx-auto">
          Each release entry links to the acceptance run that gated it.
          Add releases as builds ship to TestFlight and the App Store —
          schema is in place at <code className="bg-image-fog px-1 rounded">build_releases</code>.
        </div>
      </div>
    );
  }

  // Group by channel for display
  const byChannel: Record<string, BuildRelease[]> = {};
  for (const b of builds) {
    (byChannel[b.channel] ||= []).push(b);
  }

  return (
    <div className="space-y-3">
      {Object.entries(byChannel).map(([channel, items]) => (
        <div key={channel} className="bg-white border border-image-border rounded-xl overflow-hidden">
          <div className="bg-image-fog/60 px-3 py-2 border-b border-image-border flex items-center justify-between">
            <span className="text-sm font-bold text-image-black">{channel}</span>
            <span className="text-[10px] uppercase tracking-wider text-image-slate font-mono">
              {items.length} release{items.length === 1 ? '' : 's'}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-image-slate">
              <tr>
                <th className="text-left px-3 py-2">Build</th>
                <th className="text-left px-3 py-2">Commit</th>
                <th className="text-left px-3 py-2">Released</th>
                <th className="text-left px-3 py-2">Acceptance run</th>
                <th className="text-left px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(b => (
                <tr key={b.id} className="border-t border-image-border">
                  <td className="px-3 py-2 font-mono text-[11px] font-bold text-image-pink">
                    {b.build_number}
                    {b.is_current && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 font-bold">★ current</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] text-image-slate">
                    {b.commit_sha ? b.commit_sha.slice(0, 8) : '—'}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-image-slate">{relativeTime(b.released_at)}</td>
                  <td className="px-3 py-2 text-[11px] font-mono text-image-slate">
                    {b.acceptance_run_id ? (
                      <span className="text-image-pink">linked</span>
                    ) : (
                      <span className="text-amber-700">no acceptance run</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-image-slate italic">
                    {b.notes ?? ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Operations tab — n8n workflow status + Edge Function inventory
// ─────────────────────────────────────────────────────────────────────

// ─────────────────────────────────────────────────────────────────────
// Operations tab — platform status
// ─────────────────────────────────────────────────────────────────────

/** CELL (M28): a panel whose data is not on AWS (Cora 2026-10-29: the QA portal's test loop only). */
function NotOnAws({ what }: { what: string }) {
  return (
    <div className="bg-white border border-image-border rounded-xl p-4">
      <h2 className="text-sm font-bold uppercase tracking-wider text-image-black mb-3">{what}</h2>
      <p className="text-[12px] text-image-slate">{QA_NOT_ON_AWS}</p>
    </div>
  );
}

function OperationsTab() {
  // The n8n workflow-health panel was retired 2026-07-11 (N8N-DECOMM):
  // n8n is fully decommissioned and the n8n_workflow_status table is
  // dropped. A platform-health view (Edge Function status, queue
  // depths) can take this slot when built.
  return (
    <div className="bg-white border border-image-border rounded-xl p-4">
      <h2 className="text-sm font-bold uppercase tracking-wider text-image-black mb-3">
        Operations
      </h2>
      <p className="text-[12px] text-image-slate">
        The legacy n8n workflow-health panel was retired with the n8n
        decommission (2026-07-11). All automation now runs on Supabase
        Edge Functions.
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Utility: relative time
// ─────────────────────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

/**
 * HUB-061: every QA mutation shows its failure. The data layer (lib/qa.ts) throws the Supabase error;
 * the controls used to supply only onSuccess, so a refused run/result/defect write vanished.
 */
const errorDetail = (e: unknown): string =>
  e instanceof Error ? e.message : typeof e === 'object' && e && 'message' in e ? String((e as { message: unknown }).message) : '';

/**
 * HUB-061 (partly round) · EACH ROW'S WRITE, ITS OWN STATE. A shared useMutation only remembers its latest call, so a
 * failure on one row was hidden as soon as another row was pressed. This keeps, per row key, whether a write is out
 * and the last failure (null = none). A new press on a row clears that row's old failure; other rows keep theirs.
 */
export function useRowWrites() {
  const [inFlight, setInFlight] = useState<Record<string, number>>({});
  const [errs, setErrs] = useState<Record<string, string>>({});
  return {
    start: (k: string) => {
      setInFlight((m) => ({ ...m, [k]: (m[k] ?? 0) + 1 }));
      setErrs((m) => { const { [k]: _gone, ...rest } = m; return rest; });
    },
    fail: (k: string, e: unknown) => setErrs((m) => ({ ...m, [k]: errorDetail(e) })),
    ok: (k: string) => setErrs((m) => { const { [k]: _gone, ...rest } = m; return rest; }),
    end: (k: string) => setInFlight((m) => ({ ...m, [k]: Math.max(0, (m[k] ?? 1) - 1) })),
    pending: (k: string) => (inFlight[k] ?? 0) > 0,
    /** The row's last failure detail ('' when the server gave none), or null when the row has none. */
    error: (k: string): string | null => (k in errs ? errs[k] : null),
  };
}

/** One row's failure, said in that row. */
export function RowError({ detail, what }: { detail: string | null; what: string }) {
  if (detail === null) return null;
  return (
    <p role="alert" className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mt-1">
      {what} did not save{detail ? ` — ${detail}` : ''}. Nothing was lost; try again.
    </p>
  );
}

export function MutationError({ m, what }: { m: { isError: boolean; error: unknown }; what: string }) {
  if (!m.isError) return null;
  const detail = errorDetail(m.error);
  return (
    <p role="alert" className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
      {what} did not save{detail ? ` — ${detail}` : ''}. Nothing was lost; try again.
    </p>
  );
}

/**
 * HUB-073: "copy link" used to fire navigator.clipboard.writeText without awaiting it, so a refused
 * or missing clipboard said nothing. The write is now awaited; success says "Copied", and a refusal
 * (or no clipboard at all) shows the exact URL, selected, in a read-only box to copy by hand.
 */
export function CopyCaseLink({ caseId }: { caseId: string }) {
  const path = `/admin/qa?q=${encodeURIComponent(caseId)}#cases`;
  const [state, setState] = useState<'idle' | 'copied' | 'manual'>('idle');
  const url = `${window.location.origin}${path}`;
  return (
    <span className="ml-auto inline-flex items-center gap-2">
      <Link
        to={path}
        className="text-image-pink hover:underline"
        title="Copy a shareable link to this test case"
        onClick={() => {
          // Navigation still happens (react-router updates the URL); the copy is awaited beside it.
          const write = navigator.clipboard?.writeText?.bind(navigator.clipboard);
          if (!write) { setState('manual'); return; }
          write(url).then(() => setState('copied'), () => setState('manual'));
        }}
      >
        🔗 copy link
      </Link>
      {state === 'copied' && <span role="status" className="text-emerald-700">Copied</span>}
      {state === 'manual' && (
        <input
          readOnly
          aria-label="Link to copy"
          value={url}
          autoFocus
          onFocus={(e) => e.currentTarget.select()}
          className="px-1 py-0.5 border border-image-border rounded text-[11px] w-64"
        />
      )}
    </span>
  );
}
